import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context as StoreUserContext } from '../../providers/StoreUserContextProvider';
import { Context as HelperContext } from '../../providers/HelperContextProvider';
import {
  ECOM_PRODUCT_STATUS_ENUM,
  HEADING_SIZE_STYLE_ENUM
} from '../../config/constants';
import { triggerNotification } from '../notificationHelper';
import {
  capitalizeFirstLetter,
  formatUtcDate,
  sterilizeUrlHandle
} from '../format';
import { PRODUCT_CATEGORY_LIST } from '../../config/productCategories';

const isValidCanadianPostalCode = (code) => {
  if (!code) return false;

  const compareCode = code.trim().toUpperCase();

  const full = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/;

  return full.test(compareCode);
};

const sortVariantsByOptions = (ecomVendorProductVariants) =>
  ecomVendorProductVariants.sort((a, b) => {
    const aOptionsSorted = a.ecomVendorProductVariantOptions.sort(
      (a1, b1) => a1.nameSort - b1.nameSort
    );
    const bOptionsSorted = b.ecomVendorProductVariantOptions.sort(
      (a1, b1) => a1.nameSort - b1.nameSort
    );

    return (
      (aOptionsSorted[0]?.valueSort ?? 0) -
        (bOptionsSorted[0]?.valueSort ?? 0) ||
      (aOptionsSorted[1]?.valueSort ?? 0) -
        (bOptionsSorted[1]?.valueSort ?? 0) ||
      (aOptionsSorted[2]?.valueSort ?? 0) - (bOptionsSorted[2]?.valueSort ?? 0)
    );
  });

const getVariantOptions = (ecomStoreProduct) => {
  const variantOptions = sortVariantsByOptions(
    ecomStoreProduct?.ecomStoreProductVariants?.map(
      (v) => v.ecomVendorProductVariant
    ) || []
  )
    .reduce((r, c) => {
      c.ecomVendorProductVariantOptions.forEach((op) => {
        const existingOption = r.find((f) => f.name === op.name);
        if (existingOption) {
          if (!existingOption.values.find((f) => f.value === op.value)) {
            existingOption.values.push({
              value: op.value,
              sort: op.valueSort
            });
          }
        }
        else {
          r.push({
            sort: op.nameSort,
            name: op.name,
            values: [{ value: op.value, sort: op.valueSort }]
          });
        }
      });
      return r;
    }, [])
    .sort((a, b) => a.sort - b.sort);

  return variantOptions;
};

const isRegistrationUnavailable = (latestDivisionForm) =>
  !latestDivisionForm.active ||
  latestDivisionForm.regForm.deleted ||
  latestDivisionForm.regAssociationDivision.deleted ||
  latestDivisionForm.regAssociationDivision.regAssociationSeason.deleted ||
  new Date(formatUtcDate(latestDivisionForm.regForm.startDate)) > new Date() ||
  new Date(formatUtcDate(latestDivisionForm.regForm.endDate)) < new Date();

const isCartVariantUnavailable = (latestVariant) =>
  latestVariant.deleted ||
  latestVariant.ecomVendorProductVariant.deleted ||
  latestVariant.ecomStoreProduct.deleted ||
  latestVariant.ecomStoreProduct.ecomVendorProduct.deleted ||
  latestVariant.ecomStoreProduct.fkEcomProductStatus.toString() !==
    ECOM_PRODUCT_STATUS_ENUM.ACTIVE.toString() ||
  latestVariant.ecomStoreProduct.ecomVendorProduct.fkEcomProductStatus.toString() !==
    ECOM_PRODUCT_STATUS_ENUM.ACTIVE.toString() ||
  (latestVariant.ecomVendorProductVariant.quantity != null &&
    latestVariant.ecomVendorProductVariant.quantity <= 0);

const isStoreVariantUnavailable = (ecomStoreProduct, selectedVariant) =>
  ecomStoreProduct?.deleted ||
  ecomStoreProduct?.ecomVendorProduct.deleted ||
  selectedVariant?.deleted ||
  selectedVariant?.unavailable ||
  selectedVariant?.ecomVendorProductVariant.deleted;

const getSelectedResources = (resourceNavigation, ecomStoreTheme) => {
  if (!ecomStoreTheme) {
    return {};
  }

  const finders = {
    collections: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreCollections.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    designs: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreDesigns.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    categories: (id) =>
      PRODUCT_CATEGORY_LIST.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    products: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreProducts.find(
        (p) => p.urlHandle === id
      ),
    'sub-categories': (id) => {
      const product = ecomStoreTheme.ecomStore.ecomStoreProducts.find(
        (p) =>
          p.productType &&
          sterilizeUrlHandle(p.productType) === id.toLowerCase()
      );
      return {
        name: capitalizeFirstLetter(product?.productType || id.toLowerCase()),
        value: product?.productType.toLowerCase() || id.toLowerCase()
      };
    }
  };

  return resourceNavigation
    .filter((r) => !!r.id)
    .reduce((acc, curr) => {
      const finder = finders[curr.type];
      if (!finder) return acc;

      const resource = finder(curr.id);
      if (!resource) return { ...acc, selected: null };

      const typeKeyMap = {
        collections: 'ecomStoreCollection',
        designs: 'ecomStoreDesign',
        categories: 'ecomCategory',
        products: 'ecomStoreProduct',
        'sub-categories': 'ecomSubcategory'
      };

      const idKeyMap = {
        collections: 'pkEcomStoreCollection',
        designs: 'pkEcomStoreDesign',
        categories: 'pkEcomCategory',
        products: 'uuid',
        'sub-categories': 'value'
      };

      return {
        ...acc,
        [typeKeyMap[curr.type]]: { ...resource, id: curr.id, type: curr.type },
        selected: {
          type: curr.type,
          resource,
          name: resource.name || '',
          description: resource.description || '',
          id: resource[idKeyMap[curr.type]] || null
        }
      };
    }, {});
};

const getSelectedResourceBaseNavigationUrl = (
  resourceNavigation = [],
  currentType = ''
) =>
  resourceNavigation.reduce((r, c) => {
    if (c.type === currentType || !c.id) {
      return r;
    }

    switch (c.type) {
      case 'categories':
      case 'sub-categories':
      case 'designs':
      case 'collections':
        return `${r}/${c.type}/${c.id}`;
      default:
        return r;
    }
  }, '');

const getBreadcrumbsData = (resourceNavigation, ecomStoreTheme) => {
  const breadcrumbsData = [];
  let currentPath = '';

  const selectedResources = getSelectedResources(
    resourceNavigation,
    ecomStoreTheme
  );

  for (const item of resourceNavigation) {
    currentPath += `/${item.type.toLowerCase()}`;
    breadcrumbsData.push({
      title: capitalizeFirstLetter(item.type),
      path: currentPath
    });
    if (item.id) {
      currentPath += `/${item.id}`;
      const resourceName = Object.values(selectedResources).find(
        (v) => v?.id === item.id
      )?.name;

      const title = resourceName || item.id.replaceAll('-', ' ') || '';

      breadcrumbsData.push({
        title: capitalizeFirstLetter(title),
        path: currentPath
      });
    }
  }
  return breadcrumbsData;
};

const doesResourcesHaveProducts = (
  ecomStoreTheme,
  { pkEcomVendorDesign, pkEcomStoreCollection, pkEcomCategory, ecomSubcategory }
) =>
  (!pkEcomVendorDesign &&
    !pkEcomStoreCollection &&
    !pkEcomCategory &&
    !ecomSubcategory) ||
  ecomStoreTheme.ecomStore.ecomStoreProducts.some((p) => {
    if (
      (!pkEcomVendorDesign ||
        p.pkEcomVendorDesigns.includes(pkEcomVendorDesign)) &&
      (!pkEcomStoreCollection ||
        p.pkEcomStoreCollections.includes(pkEcomStoreCollection)) &&
      (!pkEcomCategory ||
        p.fkEcomCategory?.toString() === pkEcomCategory.toString()) &&
      (!ecomSubcategory ||
        p.productType?.toLowerCase() === ecomSubcategory.toLowerCase())
    ) {
      return true;
    }
    return false;
  });

const getWebstoreHeadingSize = (configHeadingSize) =>
  configHeadingSize === HEADING_SIZE_STYLE_ENUM.HUGE
    ? 50
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE
    ? 44
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.LARGE
    ? 36
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.MEDIUM
    ? 28
    : 22;

const getWebstoreSubHeadingSize = (configHeadingSize) =>
  configHeadingSize === HEADING_SIZE_STYLE_ENUM.HUGE
    ? 22
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE
    ? 20
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.LARGE
    ? 18
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.MEDIUM
    ? 16
    : 14;

const useWebstoreUserHelper = () => {
  const {
    state: helperState,
    toggleWebstoreAddProductToCartModal
  } = useContext(HelperContext);
  const { state, fetchEcomStoreProduct } = useContext(StoreUserContext);

  const findOrFetchStoreProduct = (
    pkEcomStore,
    { urlHandle },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const ecomStoreProduct = state.ecomStoreProductHistory.value.find(
      (f) => f.urlHandle === urlHandle
    );
    if (!ecomStoreProduct) {
      fetchEcomStoreProduct(
        pkEcomStore,
        { urlHandle },
        (data) => {
          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        (e) => {
          triggerNotification(
            'There was a problem loading the product. Please try again later.'
          );
          if (onErrorCallback) {
            onErrorCallback();
          }
        }
      );
    }
    else if (onSuccessCallback) {
      onSuccessCallback(ecomStoreProduct);
    }
  };

  const viewProductOverviewModal = (
    pkEcomStore,
    { urlHandle },
    onCallback = null
  ) => {
    findOrFetchStoreProduct(
      pkEcomStore,
      { urlHandle },
      (data) => {
        toggleWebstoreAddProductToCartModal({
          isOpen: true,
          ecomStoreProduct: data
        });
        if (onCallback) {
          onCallback();
        }
      },
      () => {
        if (onCallback) {
          onCallback();
        }
      }
    );
  };

  return {
    findOrFetchStoreProduct,
    viewProductOverviewModal
  };
};

const useWebstoreSearchFilter = ({
  editModeEnabled,
  onNavigate,
  search,
  pathname,
  setFilterStateInstance
}) => {
  const navigate = useNavigate();
  const filterSearchParams = new URLSearchParams(search);
  const searchParam = filterSearchParams.get('search');
  const sortParam = filterSearchParams.get('sort');
  const minPriceParam = filterSearchParams.get('minPrice');
  const maxPriceParam = filterSearchParams.get('maxPrice');
  const vendorParam = filterSearchParams.get('vendor');
  const includeInventory = filterSearchParams.get('includeInventory');
  const includeNoInventory = filterSearchParams.get('includeNoInventory');
  const pageIndexParam = filterSearchParams.get('page');

  const filter = {
    search: searchParam ?? '',
    sort: sortParam ?? null,
    minPrice: minPriceParam ?? null,
    maxPrice: maxPriceParam ?? null,
    vendor: vendorParam ?? null,
    includeInventory: includeInventory?.toLowerCase() === 'true',
    includeNoInventory: includeNoInventory?.toLowerCase() === 'true'
  };
  const hasSearched =
    !!filter.search ||
    !!filter.sort ||
    !!filter.minPrice ||
    !!filter.maxPrice ||
    !!filter.vendor ||
    !!includeInventory ||
    !!includeNoInventory;

  const resetFilter = () => {
    if (setFilterStateInstance) {
      setFilterStateInstance({ searchParam: '' });
    }
    else if (editModeEnabled) {
      onNavigate(pathname);
    }
    else {
      navigate(pathname, { replace: true });
    }
  };

  const objectToParams = (obj) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        params.set(key, obj[key]);
      }
    });
    return params.toString();
  };

  const onFilterChange = (params) => {
    const newParams = objectToParams({ ...filter, ...params });
    if (setFilterStateInstance) {
      setFilterStateInstance({ searchParam: `${newParams}` });
    }
    else if (editModeEnabled) {
      onNavigate(`${pathname}?${newParams}`);
    }
    else {
      navigate(`${pathname}?${newParams}`);
    }
  };

  return {
    filter,
    hasSearched,
    resetFilter,
    onFilterChange,
    pageIndex: pageIndexParam ? parseInt(pageIndexParam, 10) : 1
  };
};

const isVariantOptionUnavailable = (
  option,
  value,
  selectedOptions,
  ecomStoreProduct
) => {
  if (
    selectedOptions.some((so) => so.name === option.name && so.value === value)
  ) {
    return false;
  }

  const otherSelectedOptions = selectedOptions.filter(
    (so) => so.name !== option.name
  );

  const hasMatchingVariant = ecomStoreProduct.ecomStoreProductVariants.some(
    (variant) => {
      const variantOptions =
        variant.ecomVendorProductVariant.ecomVendorProductVariantOptions;

      return (
        otherSelectedOptions.every((op) =>
          variantOptions.some(
            (vo) => vo.name === op.name && vo.value === op.value
          )
        ) &&
        variantOptions.some(
          (vo) => vo.name === option.name && vo.value === value
        )
      );
    }
  );

  return !hasMatchingVariant;
};

const getSelectedEcomStoreProductVariant = (selectState, storeProducts) => {
  const product = storeProducts.find(
    (f) => f.pkEcomStoreProduct === selectState.pkEcomStoreProduct
  );

  const selectedVariant = product?.ecomStoreProductVariants.find(
    (v) =>
      selectState.selectedOptions.length ===
        v.ecomVendorProductVariant.ecomVendorProductVariantOptions.length &&
      v.ecomVendorProductVariant.ecomVendorProductVariantOptions.every((op) =>
        selectState.selectedOptions.some(
          (f) => f.name === op.name && f.value === op.value
        )
      )
  );

  const quantityLeft = selectedVariant
    ? selectedVariant.ecomVendorProductVariant.quantity
    : null;
  const outOfStock = quantityLeft !== null && quantityLeft <= 0;
  const tooManyInCart =
    quantityLeft != null && quantityLeft - selectState.count < 0;

  return {
    count: selectState.count,
    product,
    selectedVariant,
    isUnavailable: isStoreVariantUnavailable(product, selectedVariant),
    quantityLeft,
    outOfStock,
    tooManyInCart
  };
};

const getSelectedEcomStoreProductVariants = (
  storeProducts,
  ecomStoreCartProductsState
) => {
  const selectedVariants = ecomStoreCartProductsState
    .map((s) => getSelectedEcomStoreProductVariant(s, storeProducts))
    .filter((f) => !!f.selectedVariant)
    .map((v) => {
      const options =
        v.selectedVariant.ecomVendorProductVariant
          .ecomVendorProductVariantOptions;

      const firstOption = [...options].sort(
        (a, b) => a.nameSort - b.nameSort
      )[0];

      return {
        ...v,
        sortName: firstOption?.value ?? ''
      };
    })
    .sort((a, b) => a.sortName.localeCompare(b.sortName));

  return {
    selectedVariantsInfo: selectedVariants,
    selectedVariants: selectedVariants.map((v) => v.selectedVariant),
    hasUnavailableVariant: selectedVariants.some((v) => v.isUnavailable)
  };
};

const getEcomStoreProductSelectOptions = (
  ecomStoreProduct,
  ecomStoreCartProductsState = [],
  ecomStoreCartProduct = null
) => {
  const storeProducts = !ecomStoreProduct
    ? []
    : ecomStoreProduct.ecomStoreProductPackage
    ? [...ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts]
    : [ecomStoreProduct];

  const {
    selectedVariantsInfo,
    selectedVariants,
    hasUnavailableVariant
  } = getSelectedEcomStoreProductVariants(
    storeProducts,
    ecomStoreCartProductsState
  );

  const isBundleProduct =
    ecomStoreProduct && !!ecomStoreProduct.ecomStoreProductPackage;
  const isExternalProduct = storeProducts.some(
    (p) => p.ecomVendorProduct.ecomVendorProductExternalIds.length > 0
  );
  const isMissingProducts =
    ecomStoreProduct?.ecomStoreProductPackage &&
    ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle
      .ecomVendorBundleProducts.length !==
      ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts.length;

  const isUnavailable = isMissingProducts || hasUnavailableVariant;

  const productMedia = ecomStoreProduct
    ? isBundleProduct &&
      ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia.length === 0
      ? ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts
          .map(
            (p) =>
              p.ecomVendorProduct.ecomVendorProductMedia.sort(
                (a, b) => a.sort - b.sort
              )[0]
          )
          .filter(Boolean)
      : ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia
    : [];

  const storeProductOptions = storeProducts.reduce((acc, product) => {
    const variantOptions = getVariantOptions(product);

    const vendorVariantsSorted = sortVariantsByOptions(
      product?.ecomStoreProductVariants?.map(
        (v) => v.ecomVendorProductVariant
      ) || []
    );

    const variantsInStock = vendorVariantsSorted.filter(
      (v) =>
        !isCartVariantUnavailable({
          ecomVendorProductVariant: v,
          ecomStoreProduct: product
        })
    );
    const firstVariantWithImageAndStock = variantsInStock.find(
      (v) =>
        !!v.fkEcomVendorProductMedia &&
        product.ecomVendorProduct.ecomVendorProductMedia.some(
          (m) => m.src === v.previewImage
        )
    );
    const firstVariantInStock = variantsInStock[0];

    const defaultSelectState = {
      pkEcomStoreProduct: product.pkEcomStoreProduct,
      count: ecomStoreProduct.ecomStoreProductPackage
        ? ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts.find(
            (p) =>
              p.fkEcomVendorProduct ===
              product.ecomVendorProduct.pkEcomVendorProduct
          )?.count ?? 1
        : 1,
      fkEcomVendorProduct: product.ecomVendorProduct.pkEcomVendorProduct,
      fkEcomStoreProductPackage:
        ecomStoreProduct.ecomStoreProductPackage?.pkEcomStoreProductPackage,
      selectedOptions: firstVariantWithImageAndStock
        ? firstVariantWithImageAndStock.ecomVendorProductVariantOptions.map(
            (op) => ({
              nameSort: op.nameSort,
              name: op.name,
              value: op.value
            })
          )
        : firstVariantInStock
        ? firstVariantInStock.ecomVendorProductVariantOptions.map((op) => ({
            nameSort: op.nameSort,
            name: op.name,
            value: op.value
          }))
        : variantOptions.map((op) => ({
            nameSort: op.nameSort,
            name: op.name,
            value: op.values[0].value
          })),
      ecomStoreCartProductInputAnswers: product.ecomStoreProductInputAnswers
        .map((a) => {
          const foundInput = product.ecomVendorProduct.ecomVendorProductInputs.find(
            (i) =>
              i.fkRegFormControlType === a.fkRegFormControlType &&
              (i.pkEcomVendorProductInput === a.fkEcomVendorProductInput ||
                i.label.toLowerCase() === a.label.toLowerCase())
          );
          return foundInput
            ? {
                isAutopopulated: true,
                fkEcomVendorProductInput: foundInput.pkEcomVendorProductInput,
                fkRegFormControlType: foundInput.fkRegFormControlType,
                file: null,
                value: a.value
              }
            : null;
        })
        .filter((f) => !!f)
    };

    return [
      ...acc,
      {
        pkEcomStoreProduct: product.pkEcomStoreProduct,
        variantOptions,
        defaultSelectState,
        isBundleProduct,
        isExternalProduct,
        productMedia
      }
    ];
  }, []);

  return {
    storeProducts,
    storeProductOptions,
    productMedia,
    selectedVariantsInfo,
    selectedVariants,
    isBundleProduct,
    isExternalProduct,
    isUnavailable,
    isMissingProducts,
    hasUnavailableVariant
  };
};

export {
  isValidCanadianPostalCode,
  getVariantOptions,
  isRegistrationUnavailable,
  isCartVariantUnavailable,
  isStoreVariantUnavailable,
  getSelectedResourceBaseNavigationUrl,
  getSelectedResources,
  getBreadcrumbsData,
  getWebstoreHeadingSize,
  getWebstoreSubHeadingSize,
  doesResourcesHaveProducts,
  sortVariantsByOptions,
  useWebstoreUserHelper,
  useWebstoreSearchFilter,
  isVariantOptionUnavailable,
  getEcomStoreProductSelectOptions,
  getSelectedEcomStoreProductVariant,
  getSelectedEcomStoreProductVariants
};
